import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../lib/firebase';
import {
  doc,
  getDoc,
  updateDoc,
  writeBatch,
  onSnapshot,
  query,
  collection,
  orderBy,
  serverTimestamp,
  increment,
  deleteDoc,
  arrayRemove,
  arrayUnion,
  getDocs,
  setDoc
} from 'firebase/firestore';
import {
  X,
  MessageCircle,
  Trash2,
  Heart,
  Gift,
  Edit2
} from 'lucide-react';
import DeleteModal from './DeleteModal';
import { GifPicker } from '../feed/GifPicker';

interface Reaction {
  count: number;
  userIds: string[];
}

interface Comment {
  id: string;
  content: string;
  authorId: string;
  authorName: string;
  authorPhoto?: string;
  createdAt: Date;
  parentId: string | null;
  replyToName: string | null;
  likedBy: string[];
  likes: number;
  mediaUrl?: string;
  mediaType?: string;
  reactions?: { [key: string]: Reaction };
  postId: string;
}

interface Post {
  id: string;
  content: string;
  authorId: string;
  authorName: string;
  authorPhoto?: string;
  mediaUrls: string[];
  mediaTypes: string[];
  likes: number;
  comments: number;
  shares: number;
  likedBy: string[];
  createdAt: any;
  reactions?: { [key: string]: Reaction };
  isPinned?: boolean;
  inPinnedSection?: boolean;
}

interface CommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: Post;
  onCommentAdded?: () => void;
  onCommentDeleted?: (totalDeleted: number) => void;
  highlightedCommentId?: string | null;
}

function CommentModal({ isOpen, onClose, post, onCommentAdded, onCommentDeleted, highlightedCommentId }: CommentModalProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [replyTo, setReplyTo] = useState<Comment | null>(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [selectedGif, setSelectedGif] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; comment: Comment | null }>({
    isOpen: false,
    comment: null
  });
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [editContent, setEditContent] = useState('');

  console.log("Rendering CommentModal:", { isOpen, post, highlightedCommentId });

  const handleReply = (comment: Comment) => {
    setReplyTo(comment);
    setNewComment('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || (loading && !(newComment.trim() || selectedGif))) return;

    setLoading(true);
    const commentContent = newComment.trim();

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.exists() ? userDoc.data() : null;
      const username = userData?.username || user.email?.split('@')[0] || 'Anonymous';

      const batch = writeBatch(db);
      
      // Create a new comment document with an auto-generated ID
      const commentsRef = collection(db, `posts/${post.id}/comments`);
      const newCommentRef = doc(commentsRef);
      
      const commentData = {
        content: commentContent,
        authorId: user.uid,
        authorName: username,
        authorPhoto: user.photoURL || '',
        createdAt: serverTimestamp(),
        mediaUrl: selectedGif || null,
        mediaType: selectedGif ? 'gif' : null,
        parentId: replyTo?.id || null,
        replyToName: replyTo?.authorName || null,
        likedBy: [],
        likes: 0,
        reactions: {},
        postId: post.id
      };

      // Set the comment data
      batch.set(newCommentRef, commentData);

      // Update the post's comment count
      const postRef = doc(db, 'posts', post.id);
      batch.update(postRef, {
        comments: increment(1)
      });

      // Commit both operations
      await batch.commit();

      // Create notification if commenting on someone else's post
      if (user.uid !== post.authorId) {
        const notificationRef = doc(collection(db, 'notifications'));
        await setDoc(notificationRef, {
          type: 'comment',
          postId: post.id,
          fromUserId: user.uid,
          fromUserName: username,
          toUserId: post.authorId,
          read: false,
          createdAt: serverTimestamp(),
          content: commentContent
        });
      }

      // Only clear the form after successful commit
      setNewComment('');
      setReplyTo(null);
      setSelectedGif(null);
      
      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (comment: Comment) => {
    if (!user) return;

    try {
      // Get all replies to this comment
      const replies = getAllRepliesRecursive(comment.id);
      const totalCommentsToDelete = 1 + replies.length; // Include the parent comment

      // Delete the comment and all its replies
      const batch = writeBatch(db);
      
      // Delete the main comment
      const commentRef = doc(db, `posts/${post.id}/comments/${comment.id}`);
      batch.delete(commentRef);
      
      // Delete all replies
      replies.forEach(reply => {
        const replyRef = doc(db, `posts/${post.id}/comments/${reply.id}`);
        batch.delete(replyRef);
      });

      // Update post comment count
      const postRef = doc(db, `posts/${post.id}`);
      batch.update(postRef, {
        comments: increment(-totalCommentsToDelete)
      });

      // Execute the batch
      await batch.commit();

      // Update local state immediately
      setComments(prevComments => 
        prevComments.filter(c => 
          c.id !== comment.id && !replies.some(r => r.id === c.id)
        )
      );
      
      // Call onCommentDeleted with total number of comments deleted
      if (onCommentDeleted) {
        onCommentDeleted(totalCommentsToDelete);
      }
      
      setDeleteModal({ isOpen: false, comment: null });
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleLike = async (commentId: string) => {
    if (!user) return;
    
    try {
      const commentRef = doc(db, `posts/${post.id}/comments/${commentId}`);
      const commentDoc = await getDoc(commentRef);
      
      if (!commentDoc.exists()) return;
      
      const data = commentDoc.data();
      const likedBy = data.likedBy || [];
      const isLiked = likedBy.includes(user.uid);

      // Simple update using arrayUnion/arrayRemove
      await updateDoc(commentRef, {
        likedBy: isLiked ? arrayRemove(user.uid) : arrayUnion(user.uid),
        likes: increment(isLiked ? -1 : 1)
      });

    } catch (error) {
      console.error("Error updating like:", error);
    }
  };

  const handleEdit = async (comment: Comment) => {
    if (!user || comment.authorId !== user.uid) return;
    setEditingComment(comment);
    setEditContent(comment.content);
  };

  const handleSaveEdit = async () => {
    if (!editingComment || !user || !editContent.trim()) return;
    try {
      await updateDoc(doc(db, `posts/${post.id}/comments/${editingComment.id}`), {
        content: editContent.trim()
      });
      setEditingComment(null);
      setEditContent('');
    } catch (error) {
      console.error("Error updating comment:", error);
    }
  };

  const getAllRepliesRecursive = (commentId: string, seenIds = new Set<string>()): Comment[] => {
    const allReplies: Comment[] = [];

    comments.forEach(comment => {
      if (comment.parentId === commentId && !seenIds.has(comment.id)) {
        seenIds.add(comment.id);
        allReplies.push(comment);
        const nestedReplies = getAllRepliesRecursive(comment.id, seenIds);
        allReplies.push(...nestedReplies);
      }
    });

    return allReplies;
  };

  const getRepliesForComment = (commentId: string) => {
    return comments.filter(c => c.parentId === commentId);
  };

  const toggleCommentExpansion = (commentId: string) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const renderReplies = (parentId: string, level = 0) => {
    const replies = getRepliesForComment(parentId);
    return replies.map(reply => (
      <div key={reply.id} className="flex items-start space-x-2 mt-3">
        <img
          src={reply.authorPhoto || '/default-avatar.png'}
          alt={reply.authorName}
          className="w-8 h-8 rounded-full"
        />
        <div className="flex-1">
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
            <div className="font-semibold">{reply.authorName}</div>
            {editingComment?.id === reply.id ? (
              <div className="space-y-2">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full rounded-lg p-2 border"
                  rows={2}
                />
                <div className="flex justify-end space-x-2">
                  <button onClick={() => setEditingComment(null)} className="text-gray-600">
                    Cancel
                  </button>
                  <button onClick={handleSaveEdit} className="text-blue-500">
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{reply.content}</div>
                {reply.mediaUrl && (
                  <div className="mt-2">
                    {reply.mediaType === 'image' ? (
                      <img src={reply.mediaUrl} alt="Reply media" className="max-w-[200px] rounded-lg" />
                    ) : (
                      <div className="max-w-[200px] rounded-lg overflow-hidden">
                        <img src={reply.mediaUrl} alt="GIF" />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            <div className="flex items-center space-x-4 mt-2">
              <button
                onClick={() => handleLike(reply.id)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center space-x-1"
              >
                <Heart
                  size={16}
                  className={reply.likedBy?.includes(user?.uid || '') ? 'fill-red-500 stroke-red-500' : ''}
                />
                <span>{reply.likes || 0}</span>
              </button>
              <button
                onClick={() => handleReply(reply)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center space-x-1"
              >
                <MessageCircle size={16} />
                <span>Reply</span>
              </button>
              {(user?.uid === reply.authorId || user?.email === 'manascojohnm@gmail.com') && (
                <div className="flex items-center space-x-2">
                  {user?.uid === reply.authorId && (
                    <button
                      onClick={() => handleEdit(reply)}
                      className="text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => setDeleteModal({ isOpen: true, comment: reply })}
                    className="text-gray-600 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Show "View replies" button for nested replies */}
          {getRepliesForComment(reply.id).length > 0 && (
            <div className="mt-2">
              {!expandedComments.has(reply.id) && (
                <button
                  onClick={() => toggleCommentExpansion(reply.id)}
                  className="text-gray-600 hover:text-gray-800 text-sm font-semibold flex items-center gap-1"
                >
                  <MessageCircle size={14} />
                  View {getRepliesForComment(reply.id).length} {getRepliesForComment(reply.id).length === 1 ? 'reply' : 'replies'}
                </button>
              )}

              {expandedComments.has(reply.id) && renderReplies(reply.id, level + 1)}
            </div>
          )}
        </div>
      </div>
    ));
  };

  const renderDeleteModal = () => {
    const comment = deleteModal.comment;
    if (!comment) return null;

    return (
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, comment: null })}
        onConfirm={() => handleDeleteComment(comment)}
        title="Delete Comment"
        message={`Are you sure you want to delete this ${comment.parentId ? 'reply' : 'comment'}? This action cannot be undone.`}
      />
    );
  };

  const fetchComments = () => {
    if (!isOpen) return () => {};
    
    setIsLoadingComments(true);
    setLoadError(null);
    let unsubscribe = () => {};

    try {
      if (!user) {
        setLoadError('You must be logged in to view comments.');
        setIsLoadingComments(false);
        return unsubscribe;
      }

      const commentsRef = collection(db, `posts/${post.id}/comments`);
      const q = query(commentsRef, orderBy('createdAt', 'desc'));

      unsubscribe = onSnapshot(q, async (snapshot) => {
        const allComments = snapshot.docs.map(doc => {
          const data = doc.data();
          const likedBy = data.likedBy || [];

          return {
            id: doc.id,
            content: data.content || '',
            authorId: data.authorId || '',
            authorName: data.authorName || '',
            authorPhoto: data.authorPhoto || '',
            createdAt: data.createdAt?.toDate(),
            parentId: data.parentId || null,
            replyToName: data.replyToName || null,
            likedBy: likedBy,
            likes: likedBy.length,
            mediaUrl: data.mediaUrl || null,
            mediaType: data.mediaType || null,
            reactions: data.reactions || {},
            postId: post.id
          };
        });

        // Fix count if it's out of sync
        const actualCount = allComments.length;
        if (post.comments !== actualCount) {
          console.log(`🔧 Fixing comment count: ${post.comments} → ${actualCount}`);
          const postRef = doc(db, 'posts', post.id);
          await updateDoc(postRef, {
            comments: actualCount
          });
        }

        setComments(allComments);
      }, (error) => {
        console.error('Error fetching comments:', error);
        setLoadError('Failed to fetch comments. Please try again.');
        setIsLoadingComments(false);
      });

      setIsLoadingComments(false);
    } catch (error) {
      console.error('Error setting up comments listener:', error);
      setLoadError('Failed to set up comments listener. Please try again.');
      setIsLoadingComments(false);
    }

    return unsubscribe;
  };

  useEffect(() => {
    const unsubscribe = fetchComments();
    return () => {
      unsubscribe();
      if (!isOpen) {
        setComments([]);
        setNewComment('');
        setLoading(false);
        setReplyTo(null);
        setShowGifPicker(false);
        setSelectedGif(null);
        setIsLoadingComments(false);
        setLoadError(null);
        setExpandedComments(new Set());
      }
    };
  }, [isOpen, post.id, user]);

  const shouldTruncate = post.content.length > 300;
  const displayContent = shouldTruncate && !isExpanded 
    ? post.content.slice(0, 300) + '...'
    : post.content;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white dark:bg-gray-900 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Post & Comments</h3>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            {/* Post content section */}
            <div className="mb-6 border-b dark:border-gray-700 pb-4">
              <div className="flex items-start mb-2">
                {post.authorPhoto ? (
                  <img src={post.authorPhoto} alt={post.authorName} className="w-10 h-10 rounded-full mr-3" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 mr-3 flex items-center justify-center">
                    <span className="text-gray-500">{post.authorName[0]}</span>
                  </div>
                )}
                <div>
                  <h4 className="font-semibold">{post.authorName}</h4>
                  <div className="text-gray-600 dark:text-gray-300 whitespace-pre-line">
                    {displayContent}
                  </div>
                  {shouldTruncate && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="text-orange-500 hover:text-orange-600 text-sm mt-2"
                    >
                      {isExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              </div>
              {post.mediaUrls && post.mediaUrls.length > 0 && (
                <div className="mt-2">
                  {post.mediaUrls.map((url, index) => (
                    <img key={index} src={url} alt="Post media" className="max-w-full rounded-lg" />
                  ))}
                </div>
              )}
            </div>

            {/* Comments section */}
            <div className="max-h-[60vh] overflow-y-auto">
              {isLoadingComments ? (
                <div className="text-center py-4">Loading comments...</div>
              ) : loadError ? (
                <div className="text-red-500 text-center py-4">{loadError}</div>
              ) : comments.length === 0 ? (
                <div className="text-gray-500 text-center py-4">No comments yet</div>
              ) : (
                <div className="space-y-4">
                  {comments.filter(comment => !comment.parentId).map(comment => (
                    <div key={comment.id} className="flex items-start space-x-2 mb-4">
                      <img
                        src={comment.authorPhoto || '/default-avatar.png'}
                        alt={comment.authorName}
                        className="w-8 h-8 rounded-full"
                      />
                      <div className="flex-1">
                        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
                          <div className="font-semibold">{comment.authorName}</div>
                          {editingComment?.id === comment.id ? (
                            <div className="space-y-2">
                              <textarea
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                className="w-full rounded-lg p-2 border"
                                rows={2}
                              />
                              <div className="flex justify-end space-x-2">
                                <button onClick={() => setEditingComment(null)} className="text-gray-600">
                                  Cancel
                                </button>
                                <button onClick={handleSaveEdit} className="text-blue-500">
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{comment.content}</div>
                              {comment.mediaUrl && (
                                <div className="mt-2">
                                  {comment.mediaType === 'image' ? (
                                    <img src={comment.mediaUrl} alt="Comment media" className="max-w-[200px] rounded-lg" />
                                  ) : (
                                    <div className="max-w-[200px] rounded-lg overflow-hidden">
                                      <img src={comment.mediaUrl} alt="GIF" />
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          <div className="flex items-center space-x-4 mt-2">
                            <button
                              onClick={() => handleLike(comment.id)}
                              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center space-x-1"
                            >
                              <Heart
                                size={16}
                                className={comment.likedBy?.includes(user?.uid || '') ? 'fill-red-500 stroke-red-500' : ''}
                              />
                              <span>{comment.likes || 0}</span>
                            </button>
                            <button
                              onClick={() => handleReply(comment)}
                              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center space-x-1"
                            >
                              <MessageCircle size={16} />
                              <span>Reply</span>
                            </button>
                            {(user?.uid === comment.authorId || user?.email === 'manascojohnm@gmail.com') && (
                              <div className="flex items-center space-x-2">
                                {user?.uid === comment.authorId && (
                                  <button
                                    onClick={() => handleEdit(comment)}
                                    className="text-gray-600 hover:text-gray-800 transition-colors"
                                  >
                                    <Edit2 size={16} />
                                  </button>
                                )}
                                <button
                                  onClick={() => setDeleteModal({ isOpen: true, comment })}
                                  className="text-gray-600 hover:text-red-600 transition-colors"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-2">
                          {getRepliesForComment(comment.id).length > 0 && !expandedComments.has(comment.id) && (
                            <button
                              onClick={() => toggleCommentExpansion(comment.id)}
                              className="text-gray-600 hover:text-gray-800 text-sm font-semibold flex items-center gap-1 ml-10"
                            >
                              <MessageCircle size={14} />
                              View {getRepliesForComment(comment.id).length} {getRepliesForComment(comment.id).length === 1 ? 'reply' : 'replies'}
                            </button>
                          )}

                          {expandedComments.has(comment.id) && (
                            <div className="ml-10">
                              {renderReplies(comment.id)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {user && (
              <div className="p-4 border-t dark:border-gray-700">
                {replyTo && (
                  <div className="mb-2 text-sm text-gray-500 flex justify-between items-center">
                    <span>Replying to {replyTo.authorName}</span>
                    <button
                      onClick={() => setReplyTo(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
                {selectedGif && (
                  <div className="relative mb-2 max-w-[200px]">
                    <img
                      src={selectedGif}
                      alt="Selected GIF"
                      className="w-full h-auto rounded object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder-gif.png';
                      }}
                    />
                    <button
                      onClick={() => setSelectedGif(null)}
                      className="absolute top-1 right-1 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}

                <div className="flex gap-2">
                  <div className="flex-1 flex gap-2">
                    <form onSubmit={handleSubmit} className="flex-1 flex gap-2">
                      <input
                        type="text"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Write a comment..."
                        className="flex-1 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowGifPicker(!showGifPicker)}
                        className={`px-2 rounded border border-gray-200 ${showGifPicker ? 'bg-orange-50 text-orange-500 border-orange-200' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
                      >
                        <Gift size={18} />
                      </button>
                      <button
                        type="submit"
                        disabled={loading || !(newComment.trim() || selectedGif)}
                        className="px-3 py-1.5 rounded bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Post
                      </button>
                    </form>
                  </div>
                </div>

                {showGifPicker && (
                  <div className="fixed inset-0 z-[60]" onClick={(e) => e.stopPropagation()}>
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowGifPicker(false)} />
                    <div className="absolute inset-4 bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
                      <GifPicker
                        onClose={() => setShowGifPicker(false)}
                        onGifSelect={(gif: string) => {
                          setSelectedGif(gif);
                          setShowGifPicker(false);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            {renderDeleteModal()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommentModal;